import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, Battery, Clock, Activity, Flame, PiggyBank, Loader2,
} from 'lucide-react';
import { fetchFleetStats, formatCents, type FleetStatsResponse } from '@/lib/api';
import { useAuthStore } from '@/store/auth';

/**
 * Driver mini-dashboard (Phase 3b Item 3).
 *
 * Fleet-shell home for an attributed driver. Renders fleet-billed
 * session aggregates + an "all time" rollup + the streak signal +
 * a cost-saved estimate (so the driver perceives the perk).
 *
 * Two parallel queries (this_month + all_time) for the dual-card
 * layout the spec asks for. Each card is independent so a slow
 * aggregate on one doesn't gate the other's render.
 *
 * Linked from ProfilePage's "My Fleet" row in the Account sheet
 * (Phase 3b Item 1). Standalone route `/me/fleet-stats`.
 */

interface DriverMiniDashboardProps {
  onNavigate: (path: string) => void;
}

function durationLabel(minutes: number): string {
  if (minutes <= 0) return '0m';
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h <= 0) return `${m}m`;
  return `${h}h ${m}m`;
}

export function DriverMiniDashboard({ onNavigate }: DriverMiniDashboardProps) {
  const { t } = useTranslation('dashboard');
  const user = useAuthStore((s) => s.user);
  const monthQ = useQuery({
    queryKey: ['fleet-stats', 'this_month'],
    queryFn: () => fetchFleetStats('this_month'),
  });
  const allTimeQ = useQuery({
    queryKey: ['fleet-stats', 'all_time'],
    queryFn: () => fetchFleetStats('all_time'),
  });

  const fleetName =
    monthQ.data?.fleet_name
    ?? allTimeQ.data?.fleet_name
    ?? user?.fleet_name
    ?? t('yourFleet');

  return (
    <div
      style={{
        minHeight: '100dvh',
        background: 'var(--color-bg, #0a0a0a)',
        color: 'var(--color-tx)',
        padding: '20px 18px 80px',
      }}
    >
      <button
        type="button"
        onClick={() => onNavigate('/profile')}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'transparent', border: 'none',
          color: 'var(--color-tx-m)', cursor: 'pointer', fontSize: 13,
          marginBottom: 14, padding: 0,
        }}
      >
        <ArrowLeft size={14} /> {t('back')}
      </button>

      <header style={{ marginBottom: 18 }}>
        <p style={{
          margin: 0, fontSize: 10, letterSpacing: '1.4px',
          textTransform: 'uppercase', color: 'var(--color-tx-m)',
        }}>
          {t('yourFleet')}
        </p>
        <h1 style={{ margin: '4px 0 0', fontSize: 24 }}>{fleetName}</h1>
      </header>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <RangeCard
          title={t('range.thisMonth')}
          query={monthQ}
        />
        <RangeCard
          title={t('range.allTime')}
          query={allTimeQ}
          showAverage
        />
        <StreakCard streak={monthQ.data?.current_streak_days ?? 0} />
        <CostSavedCard
          allTimeStats={allTimeQ.data}
        />
      </div>
    </div>
  );
}

function RangeCard({
  title,
  query,
  showAverage,
}: {
  title: string;
  query: ReturnType<typeof useQuery<FleetStatsResponse, Error>>;
  showAverage?: boolean;
}) {
  const { t } = useTranslation('dashboard');
  return (
    <section style={{
      padding: '16px 18px',
      background: 'var(--color-sf2, var(--color-sf, #1a1a1a))',
      border: '1px solid var(--color-brd, var(--evi-border))',
      borderRadius: 14,
    }}>
      <p style={{
        margin: 0, fontSize: 10, letterSpacing: '1.4px',
        textTransform: 'uppercase', color: 'var(--color-tx-m)',
      }}>
        {title}
      </p>
      {query.isLoading && (
        <p style={{ marginTop: 10, color: 'var(--color-tx-m)' }}>
          <Loader2 size={14} className="spin" /> {t('range.loading')}
        </p>
      )}
      {query.data && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: showAverage ? 'repeat(3, 1fr)' : 'repeat(3, 1fr)',
          gap: 12, marginTop: 10,
        }}>
          <Stat icon={<Activity size={14} />} label={t('stat.sessions')} value={query.data.sessions_count.toString()} />
          <Stat icon={<Battery size={14} />} label="kWh" value={query.data.energy_kwh.toFixed(1)} />
          <Stat
            icon={<Clock size={14} />}
            label={showAverage ? t('stat.avgSession') : t('stat.timeCharging')}
            value={
              showAverage
                ? durationLabel(query.data.average_session_minutes)
                : durationLabel(query.data.duration_minutes)
            }
          />
        </div>
      )}
    </section>
  );
}

function Stat({
  icon, label, value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 4,
        fontSize: 10, letterSpacing: '0.8px',
        color: 'var(--color-tx-m)', textTransform: 'uppercase',
      }}>
        {icon}
        <span>{label}</span>
      </div>
      <p style={{
        margin: '4px 0 0', fontSize: 22, fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
      }}>
        {value}
      </p>
    </div>
  );
}

function StreakCard({ streak }: { streak: number }) {
  const { t } = useTranslation('dashboard');
  return (
    <section style={{
      padding: '16px 18px',
      background: streak > 0
        ? 'color-mix(in srgb, var(--evi-accent, #2ECC71) 10%, transparent)'
        : 'var(--color-sf2, var(--color-sf, #1a1a1a))',
      border: '1px solid ' + (streak > 0
        ? 'color-mix(in srgb, var(--evi-accent, #2ECC71) 35%, transparent)'
        : 'var(--color-brd, var(--evi-border))'),
      borderRadius: 14,
      display: 'flex', alignItems: 'center', gap: 14,
    }}>
      <Flame
        size={32}
        style={{
          color: streak > 0 ? 'var(--evi-accent, #2ECC71)' : 'var(--color-tx-m)',
          flexShrink: 0,
        }}
      />
      <div>
        <p style={{
          margin: 0, fontSize: 10, letterSpacing: '1.4px',
          textTransform: 'uppercase', color: 'var(--color-tx-m)',
        }}>
          {t('streak.active')}
        </p>
        <p style={{
          margin: '4px 0 0', fontSize: 22, fontWeight: 700,
        }}>
          {streak > 0
            ? t('streak.days', { count: streak })
            : t('streak.none')}
        </p>
        {streak === 0 && (
          <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--color-tx-m)' }}>
            {t('streak.hint')}
          </p>
        )}
      </div>
    </section>
  );
}

function CostSavedCard({
  allTimeStats,
}: {
  allTimeStats: FleetStatsResponse | undefined;
}) {
  const { t } = useTranslation('dashboard');
  if (!allTimeStats) {
    return (
      <section style={{
        padding: '16px 18px',
        background: 'var(--color-sf2, var(--color-sf, #1a1a1a))',
        border: '1px solid var(--color-brd, var(--evi-border))',
        borderRadius: 14,
      }}>
        <p style={{ color: 'var(--color-tx-m)', fontSize: 12 }}>
          <Loader2 size={12} className="spin" /> {t('saved.calculating')}
        </p>
      </section>
    );
  }
  const ratePerKwh = formatCents(allTimeStats.estimated_public_rate_cents_per_kwh);
  return (
    <section style={{
      padding: '16px 18px',
      background: 'var(--color-sf2, var(--color-sf, #1a1a1a))',
      border: '1px solid var(--color-brd, var(--evi-border))',
      borderRadius: 14,
      display: 'flex', alignItems: 'center', gap: 14,
    }}>
      <PiggyBank
        size={32}
        style={{ color: 'var(--evi-accent, #2ECC71)', flexShrink: 0 }}
      />
      <div>
        <p style={{
          margin: 0, fontSize: 10, letterSpacing: '1.4px',
          textTransform: 'uppercase', color: 'var(--color-tx-m)',
        }}>
          {t('saved.title')}
        </p>
        <p style={{
          margin: '4px 0 0', fontSize: 22, fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
        }}>
          {formatCents(allTimeStats.cost_saved_estimate_cents)}
        </p>
        <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--color-tx-m)', lineHeight: 1.4 }}>
          {t('saved.basis', { rate: ratePerKwh })}
        </p>
      </div>
    </section>
  );
}
